import { rangeToResampleMapping } from '@chart';
import { Alert, AlertType } from '@design/components/alert/Alert';
import type { Chart, Context, Country } from '@finos/fdc3';
import { TabsType } from '@terminal/modules/tab-content/types';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { fetchNewsFilters } from '@terminal/services/news/news-service';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { resolveEntitiesFromCsvV3 } from '@terminal/stores/use-watchlist/entityResolver';
import {
  appendChartAssetParam,
  ChartSearchParams,
} from '@terminal/utils/chart-search-utils/chart-search-utils';
import {
  COMMANDER_OPEN_TURBO_CUSTOM_EVENT,
  CommanderOpenTurboEvent,
} from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { t } from 'i18next';
import {
  contextToInstruments,
  countryMatchers,
  instrumentToResolverInput,
  timeRangeToHorizon,
} from './fdc3Mappings';

export interface Fdc3NavigateTarget {
  pathname: string;
  search: string;
}

export type Fdc3Navigate = (to: Fdc3NavigateTarget) => void;

export type Fdc3IntentHandlers = Record<
  'ViewInstrument' | 'ViewChart' | 'ViewNews',
  (context: Context) => Promise<void>
>;

interface ResolvedFdc3Entity {
  tag: string;
  defaultSnake?: string;
}

// news/v4/filters exposes countries under the `markets` key (see
// getFiltersFromActiveOptions); the extra keys guard a backend rename.
const NEWS_COUNTRY_FILTER_KEYS = new Set(['markets', 'countries', 'regions']);

const showWarningToast = (message: string) => {
  useToasts.getState().showToast({
    position: ToastPosition.TopRight,
    content: <Alert withIcon type={AlertType.Warning} message={message} />,
  });
};

// Intents can arrive before session bootstrap finishes (listeners register
// as soon as the desktop agent connects), and a tokenless API call 401s into
// apiFetch's logUserOut. Wait for bootstrap only — it always completes — then
// report whether a session exists so handlers can bail with a toast.
const waitForAuthReady = () =>
  new Promise<boolean>(resolve => {
    const settle = () => {
      const { authProcessing, token } = useAuthStore.getState();

      if (authProcessing) {
        return false;
      }

      resolve(!!token);
      return true;
    };

    if (settle()) {
      return;
    }

    const unsubscribe = useAuthStore.subscribe(() => {
      if (settle()) {
        unsubscribe();
      }
    });
  });

const ensureAuthenticated = async () => {
  const authenticated = await waitForAuthReady();

  if (!authenticated) {
    showWarningToast(t('fdc3:error.signInRequired'));
  }

  return authenticated;
};

const resolveInstruments = async (context: Context) => {
  if (!(await ensureAuthenticated())) {
    return [];
  }

  const inputs = contextToInstruments(context)
    .map(instrumentToResolverInput)
    .filter((input): input is string => Boolean(input));

  if (!inputs.length) {
    showWarningToast(t('fdc3:error.noIdentifiers'));
    return [];
  }

  const { entities } = await resolveEntitiesFromCsvV3(inputs.join(','));
  const resolved: ResolvedFdc3Entity[] = [];
  const unresolvedInputs: string[] = [];

  entities.forEach(({ primaryEntity }) => {
    if (primaryEntity.tag) {
      resolved.push({
        tag: primaryEntity.tag,
        defaultSnake: primaryEntity.default_snake,
      });
    } else {
      unresolvedInputs.push(primaryEntity.input);
    }
  });

  if (unresolvedInputs.length) {
    showWarningToast(
      t('fdc3:error.unresolved', { inputs: unresolvedInputs.join(', ') })
    );
  }

  return resolved;
};

const navigateToAssetOverviewTab = async (
  navigate: Fdc3Navigate,
  context: Context,
  tab: TabsType
) => {
  const [entity] = await resolveInstruments(context);

  if (!entity) {
    return;
  }

  const params = new URLSearchParams();
  params.set(queryKeys.assetOverviewAsset, entity.tag);
  params.set(queryKeys.assetOverviewTab, tab);

  navigate({ pathname: appPaths.assetOverview, search: params.toString() });
};

const navigateToCountryNews = async (
  navigate: Fdc3Navigate,
  country: Country
) => {
  if (!(await ensureAuthenticated())) {
    return;
  }

  const matchers = countryMatchers(country);
  const filtersResponse = await fetchNewsFilters();
  const filters = filtersResponse.data ?? [];

  for (const filter of filters) {
    if (!NEWS_COUNTRY_FILTER_KEYS.has(filter.key)) {
      continue;
    }

    const option = filter.config.options.find(
      ({ key, name }) =>
        matchers.includes(key.toLowerCase()) ||
        matchers.includes(name.toLowerCase())
    );

    if (option) {
      const params = new URLSearchParams();
      params.set(
        queryKeys.newsFilters,
        JSON.stringify({ [filter.key]: [option.key] })
      );
      navigate({ pathname: appPaths.news, search: params.toString() });
      return;
    }
  }

  showWarningToast(
    t('fdc3:error.newsUnavailable', {
      country: country.name ?? matchers[0] ?? '',
    })
  );
};

export const createFdc3IntentHandlers = (
  navigate: Fdc3Navigate
): Fdc3IntentHandlers => ({
  ViewInstrument: context =>
    navigateToAssetOverviewTab(navigate, context, TabsType.Overview),

  ViewChart: async context => {
    const resolved = await resolveInstruments(context);

    if (!resolved.length) {
      return;
    }

    const params = new URLSearchParams();

    resolved.forEach(({ tag, defaultSnake }) => {
      appendChartAssetParam(params, [tag, defaultSnake ?? `${tag}.price`, 0]);
    });

    const horizon =
      context.type === 'fdc3.chart'
        ? timeRangeToHorizon((context as Chart).range)
        : undefined;

    if (horizon) {
      params.set(ChartSearchParams.Horizon, horizon);
      params.set(ChartSearchParams.Resample, rangeToResampleMapping[horizon]);
    }

    const isOnChartPage = window.location.pathname === appPaths.chart;

    navigate({ pathname: appPaths.chart, search: params.toString() });

    // A mounted TurboChart doesn't re-read the URL; reloads are driven by
    // this event (same contract as useChartNavigate).
    if (isOnChartPage) {
      document.dispatchEvent(
        new CustomEvent(COMMANDER_OPEN_TURBO_CUSTOM_EVENT, {
          detail: { searchParams: params },
        }) as CommanderOpenTurboEvent
      );
    }
  },

  ViewNews: context => {
    if (context.type === 'fdc3.country') {
      return navigateToCountryNews(navigate, context as Country);
    }

    return navigateToAssetOverviewTab(navigate, context, TabsType.NewsInsights);
  },
});
